import { Module } from 'vuex';
import { IRootState, IAdminProjectState } from '@types';

import { ACharacterModel, ActorModel, AOrderProjectModel, AProjectModel } from '@model';

import Env from '@lib/Env';

import { ActorsApi, AProjectApi, AVideoApi } from '@/api';
import { CharacterModel } from '@/model/Admin/CharacterModel';
import { ProjectModel } from '@/model/Admin/ProjectModel';

const store: Module<IAdminProjectState, IRootState> = {
    state: {
        project: <AProjectModel>null,
        projects: <AProjectModel[]>[],
        order_project: <AOrderProjectModel>null,
        loading: <Boolean>false,
        project_types: <any>[],
        projectStatusDialog: <Boolean>false,
        projectApproveDialog: <Boolean>false,
        projectProlongDialog: <Boolean>false,
        projectSendDialog: <Boolean>false,
        projectVoiceFaceType: <String>'voice',
        projectVideoDialog: <Object>{},
        projectVideoListDialog: <Object>{ visible: false, emailRequest: null },
        projectGalleryDialog: <Object>{},
        projectActorDialog: <Object>{},
    },

    mutations: {
        setProjectZobrazitDo: (state, zobrazit_do: Date) => (state.project.zobrazit_do = zobrazit_do),
        setProjectTypes: (state, project_types: any[]) => (state.project_types = project_types),
        setProject: (state, project: AProjectModel) => (state.project = project),
        setOrderProject: (state, project: AOrderProjectModel) => (state.order_project = project),
        setProjects: (state, records: AProjectModel[]) => (state.projects = records),
        setProjectBlacklist: (state, list: any[]) => (state.project.blacklist = list),

        updateCharacterToProject: (state, character: ACharacterModel) => {
            const index = state.project.characters.findIndex((item) => item.id == character.id);
            state.project.characters[index] = character;
        },

        setProjectCharacterCandidates: (state, character_updated: ACharacterModel) => {
            const index = state.project.characters.findIndex((character) => character_updated.id == character.id);
            state.project.characters[index].candidates = character_updated.candidates;
        },
        removeCharacterFromProject: (state, character: ACharacterModel) =>
            state.project.characters.splice(state.project.characters.indexOf(character), 1),

        setOrderProjectCharacters: (state, characters: ACharacterModel[]) => {
            state.order_project.order_characters = characters;
        },

        setProjectLoading: (state, status: boolean) => (state.loading = status),

        setProjectStatusDialog: (state, val: boolean) => (state.projectStatusDialog = val),
        setProjectApproveDialog: (state, val: boolean) => (state.projectApproveDialog = val),
        setProjectProlongDialog: (state, val: boolean) => (state.projectProlongDialog = val),
        setProjectSendDialog: (state, val: boolean) => (state.projectSendDialog = val),
        setProjectVoiceFaceType: (state, val: string) => (state.projectVoiceFaceType = val),
        setProjectVideoDialog: (state, val: object) => (state.projectVideoDialog = val),
        setProjectVideoListDialog: (state, val: object) =>
            (state.projectVideoListDialog = { ...state.projectVideoListDialog, ...val }),
        setProjectGalleryDialog: (state, val: object) => (state.projectGalleryDialog = val),
        setProjectActorDialog: (state, val: object) => (state.projectActorDialog = val),
    },

    getters: {
        getProjectTypes: (state): any[] => state.project_types,
        getProject: (state): AProjectModel => state.project,
        getOrderProject: (state): AProjectModel => state.order_project,
        // getProjects: (state): AProjectModel[] => state.projects,
        getProjectCharacters: (state): ACharacterModel[] => state.project.characters,
        getProjectCharacterById: (state) => {
            return (id: number) => {
                return state.project.characters.find((character: any) => character.id === id);
            };
        },
        getProjectFormAttributes: (state): any => {
            let seconds = [];
            for (let index = 1; index <= 12; index++) {
                seconds.push({
                    name: `${index * 5} seconds`,
                    value: index,
                });
            }
            seconds.push({
                name: `Other`,
                value: 13,
            });
            return {
                types: [
                    {
                        name: 'TV spot',
                        value: 1,
                    },
                    {
                        name: 'Radio spot',
                        value: 2,
                    },
                    {
                        name: 'Dubbing',
                        value: 3,
                    },
                    {
                        name: 'Online',
                        value: 4,
                    },
                    {
                        name: 'Other',
                        value: 5,
                    },
                ],
                territories: [
                    {
                        name: 'Slovakia',
                        value: 1,
                    },
                    {
                        name: 'Other',
                        value: 2,
                    },
                ],
                timespans: [
                    {
                        name: 'till 365 days',
                        value: 1,
                    },
                    {
                        name: 'Other',
                        value: 2,
                    },
                ],
                seconds: [...seconds],
            };
        },
        getProjectType(state): any {
            return (type_id: number) =>
                state.project_types && state.project_types.find((type: any) => type.id == type_id);
        },
        getProjectTypeName(state, getters) {
            let project = state.order_project || state.project;

            if (!state.project_types) {
                return 'N/A';
            }

            const type = state.project_types.find((type: any) => type.id == project.detail.typ);

            if (!type) {
                return 'N/A';
            }

            return type.nazov;
        },

        canEditProject: (state): boolean => !state.project.hasOrder,
        canSendProject: (state): boolean =>
            !state.project.na_schvalenie && !state.project.hasOrder && !state.project.schvaleny,
        canApproveProject: (state): boolean =>
            state.project.na_schvalenie && !state.project.schvaleny && !state.project.hasOrder,
        canCloseProject: (state): boolean => state.project.schvaleny && !state.project.hasOrder,
        canProlongProject: (state): boolean =>
            (state.project.na_schvalenie || state.project.schvaleny) && !state.project.hasOrder,
        projectWasSent: (state): boolean => state.project.na_schvalenie,
        projectWasApproved: (state): boolean => state.project.schvaleny,
        projectWasClosed: (state): boolean => state.project.hasOrder,
        projectWasProlonged: (state): boolean => state.project.prolongs && state.project.prolongs.length > 0,
        projectHasCharacter: (state): boolean => state.project.characters.length > 0,
        projectIsOrderAble: (state): boolean => state.project.schvaleny || state.project.hasOrder,

        getProjectLength: (state, getters): any => {
            return (
                getters.getProjectFormAttributes.seconds.find(
                    (second: any) => second.value == state.project.detail.dlzka
                ).name || 'N/A'
            );
        },

        getProjectTeritory: (state, getters): any => {
            return (
                getters.getProjectFormAttributes.territories.find(
                    (teritory: any) => teritory.value == state.project.detail.teritory
                ).name || 'N/A'
            );
        },

        getProjectTimespan: (state, getters): any => {
            return (
                getters.getProjectFormAttributes.timespans.find(
                    (timespan: any) => timespan.value == state.project.detail.obdobie
                ).name || 'N/A'
            );
        },

        projectCharacterHasCandidates: (state) => (character: ACharacterModel) => {
            const character_found = state.project.characters.find((item) => item.id == character.id);
            return character_found.candidates.length > 0;
        },

        getProjectCharacterMp3: (state) => (character: ACharacterModel) => {
            const character_found = state.project.characters.find((item) => item.id == character.id);
            return character_found.candidates[0].mp3;
        },

        isProjectLoading: (state): Boolean => state.loading,
        getProjectStatusDialog: (state): Boolean => state.projectStatusDialog,
        getProjectApproveDialog: (state): Boolean => state.projectApproveDialog,
        getProjectProlongDialog: (state): Boolean => state.projectProlongDialog,
        getProjectSendDialog: (state): Boolean => state.projectSendDialog,
        getProjectTypeVoiceFace: (state): String => state.projectVoiceFaceType,
        getProjectVideoDialog: (state): Object => state.projectVideoDialog,
        getProjectVideoListDialog: (state): Object => state.projectVideoListDialog,
        getProjectGalleryDialog: (state): Object => state.projectGalleryDialog,
        getProjectActorDialog: (state): Object => state.projectActorDialog,
    },

    actions: {
        setCharacterFromProjectById: ({ dispatch, getters, commit }, character_id: number) => {
            const character = getters.getProject.characters.find((char: CharacterModel) => char.id == character_id);
            dispatch('setCharacter', character);
        },

        requestProjectVideo({ commit, getters }, { character_id, script, instructions, notify }: any): Promise<any> {
            return new Promise((resolve, reject) => {
                AVideoApi.requestVideo({ character_id, script, instructions, notify })
                    .then((response) => {
                        resolve(response.status);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        loadProject({ commit, getters }, project_id: number): Promise<AProjectModel> {
            if (!project_id && getters.getProject.id > 0) {
                project_id = getters.getProject.id;
            }

            return new Promise((resolve, reject) => {
                AProjectApi.loadProject(project_id)
                    .then((response) => {
                        const record = new AProjectModel(response.data.record);
                        commit('setProject', record);
                        resolve(record);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        loadProjectAndSelectCharacter({ commit, getters, dispatch }, { project_id, character_id }) {
            if (!project_id && getters.getProject.id > 0) {
                project_id = getters.getProject.id;
            }

            return new Promise((resolve, reject) => {
                AProjectApi.loadProject(project_id)
                    .then((response) => {
                        const record = new AProjectModel(response.data.record);
                        commit('setProject', record);
                        dispatch('setCharacterFromProjectById', character_id);
                        resolve(record);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        loadOrderProject({ commit }, project_id: number): Promise<AOrderProjectModel> {
            commit('setProjectLoading', true);

            return new Promise((resolve, reject) => {
                AProjectApi.loadOrderProject(project_id)
                    .then((response) => {
                        const record = new AOrderProjectModel(response.data.record);
                        commit('setOrderProject', record);
                        resolve(record);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    })
                    .finally(() => {
                        commit('setProjectLoading', false);
                    });
            });
        },

        updateOrderProject({ commit }, project: AOrderProjectModel): Promise<AOrderProjectModel> {
            commit('setProjectLoading', true);
            return new Promise((resolve, reject) => {
                AProjectApi.updateOrderProject(project)
                    .then((response) => {
                        if (!response.data.status) {
                            return;
                        }
                        const record = new AOrderProjectModel(response.data.record);
                        commit('setOrderProject', record);
                        resolve(record);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    })
                    .finally(() => {
                        commit('setProjectLoading', false);
                    });
            });
        },

        loadProjects({ commit }, client_id: number): Promise<AProjectModel[]> {
            return new Promise((resolve, reject) => {
                AProjectApi.loadProjects(client_id)
                    .then((response: any) => {
                        const records = [];
                        for (var i in response.data.records) {
                            records.push(new AProjectModel(response.data.records[i]));
                        }
                        commit('setProjects', records);
                        resolve(records);
                    })
                    .catch((err: any) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        prolongProject({ commit, getters }, project_id: number): Promise<any> {
            if (!project_id && getters.getProject.id > 0) {
                project_id = getters.getProject.id;
            }

            return new Promise((resolve, reject) => {
                AProjectApi.prolongProject(project_id)
                    .then((response: any) => {
                        if (response.data.status) {
                            commit('setProject', response.data.record);
                        }
                        resolve(response.status);
                    })
                    .catch((err: any) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        loadProjectTypes({ commit }): Promise<any[]> {
            return new Promise((resolve, reject) => {
                AProjectApi.loadProjectTypes()
                    .then((response: any) => {
                        commit('setProjectTypes', response.data.records);
                        resolve(response.data.records);
                    })
                    .catch((err: any) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        /**
         * Load neccesary data to show project correctly
         * @returns { colors, types, cities, langs }
         */
        loadProjectEssentials({ commit }): Promise<boolean> {
            return new Promise((resolve, reject) => {
                AProjectApi.loadProjectEssentials()
                    .then((response: any) => {
                        if (response.data.status) {
                            commit('setProjectTypes', response.data.types);
                            commit('setLangs', response.data.langs);
                            commit('setColors', response.data.colors);
                        }
                        resolve(response.data.status);
                    })
                    .catch((err: any) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        storeProject({ commit }, data: AProjectModel): Promise<boolean> {
            return new Promise((resolve, reject) => {
                AProjectApi.storeProject(data)
                    .then((response) => {
                        if (response.data.status) {
                            commit('setProject', response.data.record);
                        }
                        resolve(response.data.status);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        async removeProject({ rootGetters }, project: ProjectModel): Promise<any> {
            return await AProjectApi.removeProject(project);
        },

        sendApproveRequest({ commit, getters }, data: any): Promise<boolean> {
            return new Promise((resolve, reject) => {
                AProjectApi.sendApproveRequest(data)
                    .then((response) => {
                        if (response.data.status) {
                            commit('setProject', response.data.record);
                            resolve(response.data.status);
                        } else {
                            resolve(false);
                        }
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        approveProject({ commit, getters }, data: any): Promise<boolean> {
            return new Promise((resolve, reject) => {
                AProjectApi.approveProject(data)
                    .then((response) => {
                        if (response.data.status) {
                            commit('setProject', response.data.record);
                        }
                        resolve(response.data.status);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        projectAddActorToBlacklist({ commit, getters }, { actorId, projectId }): Promise<any> {
            return new Promise((resolve, reject) => {
                AProjectApi.projectAddActorToBlacklist(actorId, projectId)
                    .then((response) => {
                        if (response.data.status) {
                            commit('setProjectBlacklist', response.data.record.blacklist);
                            commit('setCharacterToActor', { voiceId: actorId, characterId: -1 });
                        }
                        resolve(response.data.status);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        projectRemoveActorFromBlacklist({ commit, getters }, { actorId, projectId }): Promise<any> {
            return new Promise((resolve, reject) => {
                AProjectApi.projectRemoveActorFromBlacklist(actorId, projectId)
                    .then((response) => {
                        if (response.data.status) {
                            commit('setProjectBlacklist', response.data.record.blacklist);
                            commit('setCharacterToActor', { voiceId: actorId, characterId: null });
                        }
                        resolve(response.data.status);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        async openActorDetail({ commit }, actor: ActorModel): Promise<any> {
            const { data } = await ActorsApi.load(actor.id, {
                include: [
                    'nominations',
                    'nominations.project',
                    'nominations.projectType',
                    'rating',
                    'photos',
                    'portfolio',
                    'prices',
                ],
            });
            commit('setProjectActorDialog', { visible: true, actor: new ActorModel(data) });
        },
    },
};

export default store;
